import { IconArrowLeft, IconPrinter } from '@tabler/icons-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Banner, Spinner, useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import type { Consent } from '@/types/api';
import { useAuth } from '../auth';
import { useDoctor } from '../doctors';
import { useClinicSettings, useLogoUrl } from '../settings';
import { consentsApi } from './api';
import { ConsentEditor } from './components/ConsentEditor';
import { ConsentPaper } from './components/ConsentPaper';

function Header({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <div className="page-head no-print">
      <button
        type="button"
        className="icon-btn"
        onClick={() => navigate({ name: 'consents' })}
        aria-label={t('consents.back')}
      >
        <IconArrowLeft size={24} />
      </button>
      <div className="page-head__title">
        <h1>{title}</h1>
      </div>
    </div>
  );
}

/** A new consent (`id` 'new'), or a given one: shown as printed, to print again. Never edited. */
export function ConsentPage({ id, patientId }: { id: string; patientId?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const { doctorId } = useDoctor();
  const clinic = useClinicSettings(user?.clinic?.id ?? '');
  const logoUrl = useLogoUrl(clinic?.logoVersion ?? null);
  const isNew = id === 'new';
  const consent = useResource<Consent | null>(
    () => (isNew ? Promise.resolve(null) : consentsApi.get(id)),
    id
  );
  const [busy, setBusy] = useState(false);

  if (isNew) {
    return (
      <div className="stack offer-page">
        <Header title={t('consents.new')} />
        <ConsentEditor
          patientId={patientId}
          busy={busy}
          onCancel={() => navigate({ name: 'consents' })}
          onSave={async (input) => {
            setBusy(true);
            try {
              const created = await consentsApi.create({ ...input, dentistId: doctorId });
              toast.show(t('consents.saved'));
              navigate({ name: 'consent', id: created.id }, true);
            } catch (err) {
              toast.show(errorMessage(err), 'error');
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
    );
  }

  if (consent.error) {
    return (
      <>
        <Header title={t('consents.title')} />
        <Banner tone="error">{errorMessage(consent.error)}</Banner>
      </>
    );
  }
  if (!consent.data) return <Spinner />;
  const current = consent.data;
  return (
    <div className="stack offer-page">
      <Header title={t('consents.numberTitle', { number: current.number })} />
      <div className="row no-print">
        <button type="button" className="btn btn--dark" onClick={() => window.print()}>
          <IconPrinter size={18} /> {t('consents.print')}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => navigate({ name: 'patient', id: current.patient.id })}
        >
          {t('consents.openChart')}
        </button>
      </div>
      <div className="offer-preview">
        <ConsentPaper consent={current} clinic={clinic} logoUrl={logoUrl} />
      </div>
      {/* The printed copy lives outside the app shell, so nothing else reaches the paper. */}
      {createPortal(
        <div className="print-root">
          <ConsentPaper consent={current} clinic={clinic} logoUrl={logoUrl} />
        </div>,
        document.body
      )}
    </div>
  );
}
